import { useState } from 'react';
import { Alert, AlertTitle, Box, Button } from '@mui/material';

import { authApi } from '../../services/authApi';
import { useAuthStore } from '../../stores/authStore';

/**
 * Dashboard banner shown to a signed-in user whose email is not yet verified
 * (#209). Offers a "Resend verification email" button. Verification is
 * informational at MVP — this never blocks the app, it just nudges.
 *
 * Renders nothing when the user is unauthenticated or already verified.
 * Accessible: the unverified prompt is a `role="region"` alert and the resend
 * confirmation is a polite `role="status"`.
 */
export function UnverifiedEmailBanner() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const emailVerified = useAuthStore((s) => s.emailVerified);

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isAuthenticated || emailVerified) return null;

  async function onResend() {
    setSending(true);
    setError(null);
    try {
      await authApi.resendVerification();
      setSent(true);
    } catch {
      setError('We could not send the email just now. Please try again in a moment.');
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <Alert severity="success" role="status" sx={{ mb: 3 }}>
        If your email is unverified, a new verification link is on its way. Check your inbox.
      </Alert>
    );
  }

  return (
    <Alert
      severity="warning"
      role="region"
      aria-label="Email not verified"
      sx={{ mb: 3 }}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={onResend}
          disabled={sending}
          aria-busy={sending}
        >
          {sending ? 'Sending…' : 'Resend verification email'}
        </Button>
      }
    >
      <AlertTitle>Verify your email</AlertTitle>
      Your email address isn’t verified yet. Verifying helps secure your account and keeps your
      alert notifications reliable.
      {error ? (
        <Box component="span" sx={{ display: 'block', mt: 1 }} role="alert">
          {error}
        </Box>
      ) : null}
    </Alert>
  );
}

export default UnverifiedEmailBanner;
